import { Anchor, alpha, Box, Flex, Text } from '@mantine/core';
import { IconArrowRight, IconClock } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { isDefined } from 'remeda';
import { useSafeLocalizedCompactCurrencyFormatter } from 'shared/utils/currency';

import { type PaidByReminderSegment, segmentKey } from './paid-by-reminder-segments';
import { formatPercentage } from './percentage';

const HOVER_TINT_ALPHA = 0.15;
const ROW_PADDING = 'var(--mantine-spacing-xs) var(--mantine-spacing-space3)';
const ROW_RULE = '1px solid var(--mantine-color-purple-1)';
const ROW_RADIUS = 10;
const TINT_TRANSITION = 'background-color 180ms cubic-bezier(.2, .7, .2, 1)';

const ROW_GRID = {
  gridTemplateColumns: '14px 1fr 52px 84px 76px',
  gap: 'var(--mantine-spacing-xs)',
  alignItems: 'center',
} as const;

type PaidByReminderLegendProps = {
  segments: ReadonlyArray<PaidByReminderSegment>;
  hoveredKey: string | null;
  stillOpenTo?: string;
};

export const PaidByReminderLegend = ({
  segments,
  hoveredKey,
  stillOpenTo,
}: PaidByReminderLegendProps) => {
  const { t } = useTranslation();
  const formatAmount = useSafeLocalizedCompactCurrencyFormatter();
  const [isCalloutPointedAt, setIsCalloutPointedAt] = useState(false);
  const settled = segments.filter(segment => segment.kind === 'step');
  const stillOpen = segments.filter(segment => segment.kind === 'open' && segment.count > 0);

  const tintOf = (segment: PaidByReminderSegment) => ({
    backgroundColor:
      segmentKey(segment) === hoveredKey ? alpha(segment.color, HOVER_TINT_ALPHA) : 'transparent',
    borderRadius: ROW_RADIUS,
    transition: TINT_TRANSITION,
    padding: ROW_PADDING,
  });

  return (
    <>
      <Flex component="ul" direction="column" style={{ padding: 0, listStyleType: 'none' }}>
        {settled.map((segment, index) => (
          <Box
            key={segmentKey(segment)}
            component="li"
            aria-current={segmentKey(segment) === hoveredKey || undefined}
            style={{
              ...tintOf(segment),
              borderTop: index === 0 ? '1px solid transparent' : ROW_RULE,
            }}
          >
            <Box display="grid" style={ROW_GRID}>
              <Box
                aria-hidden
                w={10}
                h={10}
                style={{ borderRadius: 3, backgroundColor: segment.color }}
              />
              <Text size="sm" c="gray.8">
                {segment.label}
              </Text>
              <Text size="sm" c="gray.6">
                {formatPercentage(segment.share)}
              </Text>
              <Text size="sm" c="gray.6">
                {t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.INVOICE_COUNT, { count: segment.count })}
              </Text>
              <Text size="sm" fw={600} c="gray.8" ta="right">
                {formatAmount.format(segment.amount)}
              </Text>
            </Box>
          </Box>
        ))}
      </Flex>
      {stillOpen.map(segment => {
        const figures = (
          <Flex align="center" wrap="wrap" gap="sm">
            <Flex align="center" gap="space2" c="coral.8" style={{ flex: '1 1 240px' }}>
              <IconClock size={15} stroke={1.9} />
              <Text size="sm" fw={600} c="coral.8">
                {segment.label}
                {isDefined(stillOpenTo) && (
                  <IconArrowRight size={14} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                )}
              </Text>
            </Flex>
            <Text size="sm" c="gray.6">
              {formatPercentage(segment.share)}
            </Text>
            <Text size="sm" c="gray.6">
              {t(i18nKeys.ANALYTICS.PAID_BY_REMINDER.INVOICE_COUNT, { count: segment.count })}
            </Text>
            <Text size="sm" fw={600} c="gray.8">
              {formatAmount.format(segment.amount)}
            </Text>
          </Flex>
        );

        return (
          <Box
            key={segmentKey(segment)}
            mt="sm"
            aria-current={segmentKey(segment) === hoveredKey || undefined}
            onMouseEnter={() => setIsCalloutPointedAt(true)}
            onMouseLeave={() => setIsCalloutPointedAt(false)}
            style={{
              padding: 'var(--mantine-spacing-space3) var(--mantine-spacing-space4)',
              borderRadius: 12,
              border: `1px dashed var(--mantine-color-coral-${isCalloutPointedAt ? 6 : 4})`,
              backgroundColor:
                isCalloutPointedAt || segmentKey(segment) === hoveredKey
                  ? 'var(--mantine-color-coral-2)'
                  : 'var(--mantine-color-coral-1)',
              transition: `${TINT_TRANSITION}, border-color 180ms cubic-bezier(.2, .7, .2, 1)`,
            }}
          >
            {isDefined(stillOpenTo) ? (
              <Anchor component={Link} to={stillOpenTo} underline="never" display="block">
                {figures}
              </Anchor>
            ) : (
              figures
            )}
          </Box>
        );
      })}
    </>
  );
};
